import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, CalendarClock, ClipboardList, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Checkbox } from '@/components/ui/Checkbox';
import { Label } from '@/components/ui/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { useIsMobile } from '@/lib/useMediaQuery';
import { useLocations } from '@/features/inventory/hooks';
import { fmtQty } from '@/features/manufacturing/planning/engine';
import { monthKey } from '@/features/manufacturing/planning/dates';
import {
  useCreatePlannedPurchaseOrders,
  type CreatedPlannedPo,
  type PlannedPoInput,
} from '@/features/manufacturing/planning/hooks';
import { buildPoDrafts, type PoDraft, type PoDraftLine } from '@/features/manufacturing/planning/poDrafts';
import type { PlannedOrder, PlanningModel } from '@/features/manufacturing/planning/types';
import { formatCurrency, formatUnitCurrency } from '@/lib/formatters';
import { formatQtyUom } from '@/lib/uom';
import { cn } from '@/lib/utils';
import { monthLabel, OrderStatusBadge, shortDate } from './shared';

/**
 * Limit the dialog to a hand-picked set of suggestions — the procurement
 * calendar uses this for its per-vendor / per-day "Create PO" buttons.
 * Without a scope the dialog offers everything in a time window.
 */
export interface CreatePurchaseOrdersScope {
  orderIds: string[];
  /** e.g. "Shenzhen Motors · place by Nov 4" */
  heading: string;
}

interface CreatePurchaseOrdersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  model: PlanningModel;
  canCreate: boolean;
  scope?: CreatePurchaseOrdersScope | null;
  onCreated?: (created: CreatedPlannedPo[]) => void;
}

const WINDOWS: Array<{ value: string; label: string; days: number | null }> = [
  { value: '7', label: 'Due in the next 7 days', days: 7 },
  { value: '14', label: 'Due in the next 2 weeks', days: 14 },
  { value: '30', label: 'Due in the next 30 days', days: 30 },
  { value: '60', label: 'Due in the next 60 days', days: 60 },
  { value: 'all', label: 'Everything in the horizon', days: null },
];

function defaultWindow(model: PlanningModel): string {
  // Start with the tightest window that still has something to order.
  for (const w of WINDOWS) {
    const limit = w.days;
    if (limit == null) return w.value;
    const count = model.orders.filter((o) => {
      const ideal = o.idealOrderDate ?? model.today;
      const daysOut = Math.round((Date.parse(ideal) - Date.parse(model.today)) / 86_400_000);
      return daysOut <= limit;
    }).length;
    if (count > 0) return w.value;
  }
  return 'all';
}

/** "4 rolls" with "= 7,200 in" underneath, or just "7,200" when bought in the stock unit. */
function lineQty(line: PoDraftLine, baseUom: string | undefined): { main: string; stock: string | null } {
  if (!line.purchaseUom) return { main: fmtQty(line.quantity), stock: null };
  return {
    main: formatQtyUom(line.purchaseQuantity, line.purchaseUom),
    stock: `= ${formatQtyUom(line.quantity, baseUom ?? '')}`,
  };
}

function lineTotal(line: PoDraftLine): number | null {
  return line.purchaseUnitCostCents == null ? null : Math.round(line.purchaseUnitCostCents * line.purchaseQuantity);
}

function PriceBreakToggle({
  option,
  checked,
  onChange,
  purchaseUom,
  conversion,
}: {
  option: NonNullable<PlannedOrder['priceBreakOption']>;
  checked: boolean;
  onChange: (on: boolean) => void;
  purchaseUom: string | null;
  conversion: number;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2 text-xs">
      <Checkbox
        checked={checked}
        onCheckedChange={onChange}
        ariaLabel="Use price break"
        className="mt-0.5"
      />
      <span>
        <span className="font-medium">
          {purchaseUom
            ? `${formatQtyUom(option.purchaseQuantity, purchaseUom)} @ ${formatCurrency(
                Math.round(option.unitCostCents * conversion),
              )}/${purchaseUom}`
            : `${fmtQty(option.quantity)} @ ${formatCurrency(option.unitCostCents)}`}
        </span>
        <span className="ml-1 text-muted-foreground">
          (−{Math.round(option.unitSavingsPct * 100)}%/unit)
        </span>
        <br />
        {option.recommended ? (
          <span className="inline-flex items-center gap-1 text-bright-moss-dark">
            <Sparkles className="h-3 w-3" /> Cheaper in total
          </span>
        ) : (
          <span className="text-muted-foreground">
            +{formatCurrency(option.extraCashCents)} cash · +{option.extraCoverageDays}d of stock
          </span>
        )}
      </span>
    </label>
  );
}

function DraftCard({
  draft,
  model,
  excluded,
  isMobile,
  onToggleLine,
  onTogglePriceBreak,
}: {
  draft: PoDraft;
  model: PlanningModel;
  excluded: Set<string>;
  isMobile: boolean;
  onToggleLine: (orderId: string, include: boolean) => void;
  onTogglePriceBreak: (orderId: string, on: boolean) => void;
}) {
  const overdue = draft.orderByDate <= model.today;
  return (
    <div className="rounded-xl border border-border bg-card">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-4 py-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-medium">{draft.vendorName}</h3>
            <Badge variant="muted">
              {draft.lines.length} {draft.lines.length === 1 ? 'line' : 'lines'}
            </Badge>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span
              className={cn('inline-flex items-center gap-1', overdue && 'text-fresh-salmon-dark')}
            >
              <CalendarClock className="h-3 w-3" />
              Place by <strong className="font-medium">{shortDate(draft.orderByDate)}</strong>
              {overdue && ' (today)'}
            </span>
            {draft.idealOrderDate !== draft.orderByDate && (
              <span>Ideal {shortDate(draft.idealOrderDate)}</span>
            )}
            {draft.expectedAt && <span>Lands ~{shortDate(draft.expectedAt)} if placed today</span>}
            {draft.leadTimeDays != null && <span>{draft.leadTimeDays}d lead time</span>}
          </div>
        </div>
        <div className="text-right">
          <div className="text-sm font-medium tabular-nums">{formatCurrency(draft.totalCents)}</div>
          {draft.linesWithoutCost > 0 && (
            <div className="text-xs text-fresh-salmon-dark">
              {draft.linesWithoutCost} {draft.linesWithoutCost === 1 ? 'line has' : 'lines have'} no
              cost
            </div>
          )}
        </div>
      </div>
      {isMobile ? (
        <ul className="divide-y divide-border">
          {draft.lines.map((line) => {
            const { order, quantity, usingPriceBreak } = line;
            const plan = model.itemsById.get(order.itemId);
            const included = !excluded.has(order.id);
            const qty = lineQty(line, plan?.item.baseUom);
            const total = lineTotal(line);
            return (
              <li key={order.id} className={cn('space-y-2 px-4 py-3', !included && 'opacity-50')}>
                <div className="flex items-start gap-3">
                  <Checkbox
                    checked={included}
                    onCheckedChange={(v) => onToggleLine(order.id, v)}
                    ariaLabel={`Include ${plan?.item.sku ?? 'part'}`}
                    className="mt-1"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate font-medium">
                          {plan?.item.name ?? order.itemId}
                        </div>
                        <div className="font-mono text-[11px] text-muted-foreground">
                          {plan?.item.sku}
                        </div>
                      </div>
                      <div className="shrink-0 text-right tabular-nums">
                        <div className="font-medium">{qty.main}</div>
                        {qty.stock && <div className="text-[11px] text-muted-foreground">{qty.stock}</div>}
                        <div className="text-xs text-muted-foreground">
                          {total == null ? 'no cost' : formatCurrency(total)}
                        </div>
                      </div>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <OrderStatusBadge status={order.status} />
                      <span>needed {shortDate(order.needByDate)}</span>
                      {quantity !== order.netRequirement && (
                        <span>
                          need {fmtQty(order.netRequirement)}
                          {order.moqApplied && ' · MOQ'}
                          {order.multipleApplied && ' · pack'}
                        </span>
                      )}
                    </div>
                    {order.priceBreakOption && (
                      <div className="mt-2">
                        <PriceBreakToggle
                          option={order.priceBreakOption}
                          checked={usingPriceBreak}
                          onChange={(v) => onTogglePriceBreak(order.id, v)}
                          purchaseUom={line.purchaseUom}
                          conversion={line.conversion}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead>Part</TableHead>
              <TableHead>Needed by</TableHead>
              <TableHead className="text-right">Qty</TableHead>
              <TableHead className="text-right">Unit</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead>Price break</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {draft.lines.map((line) => {
              const { order, quantity, usingPriceBreak } = line;
              const plan = model.itemsById.get(order.itemId);
              const pb = order.priceBreakOption;
              const qty = lineQty(line, plan?.item.baseUom);
              const total = lineTotal(line);
              return (
                <TableRow key={order.id} className={cn(excluded.has(order.id) && 'opacity-50')}>
                  <TableCell>
                    <Checkbox
                      checked={!excluded.has(order.id)}
                      onCheckedChange={(v) => onToggleLine(order.id, v)}
                      ariaLabel={`Include ${plan?.item.sku ?? 'part'}`}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{plan?.item.sku ?? order.itemId}</div>
                    <div className="max-w-[18rem] truncate text-xs text-muted-foreground">
                      {plan?.item.name}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <span className="text-sm tabular-nums">{shortDate(order.needByDate)}</span>
                      <OrderStatusBadge status={order.status} className="w-fit" />
                    </div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    <div>{qty.main}</div>
                    {qty.stock && <div className="text-[10px] text-muted-foreground">{qty.stock}</div>}
                    {quantity !== order.netRequirement && (
                      <div className="text-[10px] text-muted-foreground">
                        need {fmtQty(order.netRequirement)}
                        {order.moqApplied && ' · MOQ'}
                        {order.multipleApplied && ' · pack'}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {line.purchaseUnitCostCents == null ? (
                      <span className="text-fresh-salmon-dark">—</span>
                    ) : (
                      <>
                        {formatUnitCurrency(line.purchaseUnitCostCents)}
                        {line.purchaseUom && (
                          <span className="text-[10px] text-muted-foreground">/{line.purchaseUom}</span>
                        )}
                      </>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {total == null ? '—' : formatCurrency(total)}
                  </TableCell>
                  <TableCell>
                    {pb ? (
                      <PriceBreakToggle
                        option={pb}
                        checked={usingPriceBreak}
                        onChange={(v) => onTogglePriceBreak(order.id, v)}
                        purchaseUom={line.purchaseUom}
                        conversion={line.conversion}
                      />
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

/**
 * Turn the planner's suggestions into draft POs, one per vendor. The
 * user picks a window, unticks what they don't want, opts into price
 * breaks, and gets internal_po drafts with an order-by date to approve
 * on the Purchase orders page.
 */
export function CreatePurchaseOrdersDialog({
  open,
  onOpenChange,
  model,
  canCreate,
  scope = null,
  onCreated,
}: CreatePurchaseOrdersDialogProps) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const createPos = useCreatePlannedPurchaseOrders();
  const { data: locations } = useLocations();

  const [windowValue, setWindowValue] = useState(() => defaultWindow(model));
  const [excluded, setExcluded] = useState<Set<string>>(() => new Set());
  const [usePriceBreak, setUsePriceBreak] = useState<Set<string>>(
    () => new Set(model.orders.filter((o) => o.priceBreakOption?.recommended).map((o) => o.id)),
  );
  const [shipTo, setShipTo] = useState<string>('');

  useEffect(() => {
    if (!open) return;
    setWindowValue(defaultWindow(model));
    setExcluded(new Set());
    setUsePriceBreak(
      new Set(model.orders.filter((o) => o.priceBreakOption?.recommended).map((o) => o.id)),
    );
  }, [open, model, scope]);

  useEffect(() => {
    if (shipTo || !locations?.length) return;
    const warehouse = locations.find((l) => l.kind === 'warehouse') ?? locations[0];
    setShipTo(warehouse.id);
  }, [locations, shipTo]);

  // Scoped: ignore the window and hide every suggestion outside the scope.
  const dueWithinDays = scope ? null : (WINDOWS.find((w) => w.value === windowValue)?.days ?? null);
  const { drafts, unassignable } = useMemo(() => {
    const hidden = new Set(excluded);
    if (scope) {
      const keep = new Set(scope.orderIds);
      for (const order of model.orders) if (!keep.has(order.id)) hidden.add(order.id);
    }
    const result = buildPoDrafts(model, { dueWithinDays, usePriceBreakFor: usePriceBreak, excluded: hidden });
    // One entry per part, not per suggested order.
    const seen = new Set<string>();
    const unassignable = result.unassignable.filter((o) => {
      if (seen.has(o.itemId)) return false;
      seen.add(o.itemId);
      return true;
    });
    return { drafts: result.drafts, unassignable };
  }, [model, scope, dueWithinDays, usePriceBreak, excluded]);

  const totals = useMemo(() => {
    let cents = 0;
    let lines = 0;
    let withoutCost = 0;
    for (const d of drafts) {
      cents += d.totalCents;
      lines += d.lines.length;
      withoutCost += d.linesWithoutCost;
    }
    return { cents, lines, withoutCost };
  }, [drafts]);

  // Cash check against this month's budget: what is already committed or
  // drafted this month, plus what we are about to add.
  const budget = model.settings.monthlyPurchasingBudgetCents;
  const thisMonth = monthKey(model.today);
  const bucket = model.cash.buckets.find((b) => b.key === thisMonth);
  const alreadyThisMonth = (bucket?.committedCents ?? 0) + (bucket?.draftCents ?? 0);
  const overBudgetBy = budget != null ? alreadyThisMonth + totals.cents - budget : 0;

  const toggleLine = (orderId: string, include: boolean) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (include) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  const togglePriceBreak = (orderId: string, on: boolean) => {
    setUsePriceBreak((prev) => {
      const next = new Set(prev);
      if (on) next.add(orderId);
      else next.delete(orderId);
      return next;
    });
  };

  const handleCreate = async () => {
    if (drafts.length === 0) return;
    const inputs: PlannedPoInput[] = drafts.map((draft) => ({
      vendor_id: draft.vendorId,
      order_by_date: draft.orderByDate,
      expected_at: draft.expectedAt,
      lead_time_days: draft.leadTimeDays,
      ship_to_location_id: shipTo || null,
      notes: `Generated by production planning on ${model.today}. Place with the vendor by ${draft.orderByDate}.`,
      lines: draft.lines.map(
        ({ order, purchaseQuantity, purchaseUnitCostCents, purchaseUom, conversion, usingPriceBreak }) => {
        const plan = model.itemsById.get(order.itemId);
        const bits = [
          plan?.item.name ?? null,
          `covers ${order.coversFromDate} → ${order.coverageEndDate}`,
          usingPriceBreak ? 'price-break quantity' : null,
          order.moqApplied ? 'MOQ' : null,
          order.multipleApplied ? 'pack size' : null,
        ].filter(Boolean);
        return {
          item_id: order.itemId,
          quantity: purchaseQuantity,
          unit_price_cents: purchaseUnitCostCents,
          uom: purchaseUom ?? plan?.item.baseUom ?? null,
          conversion_to_base: purchaseUom ? conversion : 1,
          description: bits.join(' · '),
        };
        },
      ),
    }));
    try {
      const created = await createPos.mutateAsync(inputs);
      const openTarget =
        created.length === 1
          ? `/procurement/purchase-orders?po=${created[0].id}`
          : '/procurement/purchase-orders';
      toast.success(
        created.length === 1
          ? `Draft ${created[0].po_number} created.`
          : `${created.length} draft POs created: ${created.map((c) => c.po_number).join(', ')}.`,
        {
          action: {
            label: created.length === 1 ? 'Open' : 'View POs',
            onClick: () => navigate(openTarget),
          },
        },
      );
      onCreated?.(created);
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not create the purchase orders.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-5xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {scope && drafts.length === 1 ? 'Create draft purchase order' : 'Create draft purchase orders'}
          </DialogTitle>
          <DialogDescription>
            {scope ? (
              <>
                <span className="font-medium text-foreground">{scope.heading}</span>
                {' — '}
                untick anything you don’t want ordered.{' '}
                {drafts.length === 1
                  ? 'The draft lands on the Purchase orders page as an internal PO for approval; nothing is sent to the vendor from here.'
                  : 'One draft per vendor lands on the Purchase orders page as internal POs for approval; nothing is sent to a vendor from here.'}
              </>
            ) : (
              <>
                One draft per vendor, with the day it has to be placed. Drafts land on the Purchase
                orders page as internal POs for approval; nothing is sent to a vendor from here.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className={cn('mt-4 grid gap-3', !scope && 'sm:grid-cols-2')}>
          {!scope && (
            <div className="space-y-1.5">
              <Label htmlFor="po-window">Which suggestions</Label>
              <Select value={windowValue} onValueChange={setWindowValue}>
                <SelectTrigger id="po-window">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WINDOWS.map((w) => (
                    <SelectItem key={w.value} value={w.value}>
                      {w.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                By ideal order date. Late and order-now lines are always included.
              </p>
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="po-ship-to">Ship to</Label>
            <Select value={shipTo} onValueChange={setShipTo}>
              <SelectTrigger id="po-ship-to">
                <SelectValue placeholder="Pick a location" />
              </SelectTrigger>
              <SelectContent>
                {(locations ?? []).map((l) => (
                  <SelectItem key={l.id} value={l.id}>
                    {l.code} — {l.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {budget != null && (
          <div
            className={cn(
              'mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border px-3 py-2 text-xs',
              overBudgetBy > 0
                ? 'border-fresh-salmon/60 bg-fresh-salmon/15 text-fresh-salmon-dark'
                : 'border-border bg-secondary/60 text-muted-foreground',
            )}
          >
            {overBudgetBy > 0 && <AlertTriangle className="h-3.5 w-3.5" />}
            <span>
              {monthLabel(thisMonth)} budget {formatCurrency(budget)} · already committed{' '}
              {formatCurrency(alreadyThisMonth)} · these drafts {formatCurrency(totals.cents)}
            </span>
            {overBudgetBy > 0 ? (
              <span className="font-medium">
                Over by {formatCurrency(overBudgetBy)} — untick lines or skip price breaks.
              </span>
            ) : (
              <span>
                {formatCurrency(budget - alreadyThisMonth - totals.cents)} left this month.
              </span>
            )}
          </div>
        )}

        <div className="mt-4 space-y-4">
          {drafts.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">
              {model.orders.length === 0
                ? 'Nothing to order: every part is covered through the horizon.'
                : scope
                  ? 'Every line is unticked — tick at least one to draft a PO.'
                  : 'No suggestions in this window. Widen it above.'}
            </div>
          ) : (
            drafts.map((draft) => (
              <DraftCard
                key={draft.vendorId}
                draft={draft}
                model={model}
                excluded={excluded}
                isMobile={isMobile}
                onToggleLine={toggleLine}
                onTogglePriceBreak={togglePriceBreak}
              />
            ))
          )}

          {unassignable.length > 0 && (
            <div className="rounded-lg border border-fresh-salmon/60 bg-fresh-salmon/10 px-3 py-2 text-xs text-fresh-salmon-dark">
              <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />
              {unassignable.length} {unassignable.length === 1 ? 'part has' : 'parts have'} no
              supplier, so no PO can be drafted:{' '}
              {unassignable
                .slice(0, 5)
                .map((o) => model.itemsById.get(o.itemId)?.item.sku ?? o.itemId)
                .join(', ')}
              {unassignable.length > 5 && ` and ${unassignable.length - 5} more`}. Add one from the
              Missing info tab.
            </div>
          )}
        </div>

        <DialogFooter className="mt-6 flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-muted-foreground">
            {drafts.length} {drafts.length === 1 ? 'PO' : 'POs'} · {totals.lines}{' '}
            {totals.lines === 1 ? 'line' : 'lines'} ·{' '}
            <span className="font-medium text-foreground">{formatCurrency(totals.cents)}</span>
            {totals.withoutCost > 0 && ` (+${totals.withoutCost} uncosted)`}
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            {canCreate ? (
              <Button
                type="button"
                onClick={handleCreate}
                disabled={drafts.length === 0 || createPos.isPending}
              >
                <ClipboardList className="h-4 w-4" />
                {createPos.isPending
                  ? 'Creating…'
                  : `Create ${drafts.length || ''} draft ${drafts.length === 1 ? 'PO' : 'POs'}`}
              </Button>
            ) : (
              <span className="text-xs text-muted-foreground">
                Only admins can create POs.{' '}
                <Link to="/procurement/purchase-orders" className="text-info hover:underline">
                  View purchase orders
                </Link>
              </span>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
